import { API_ENDPOINTS } from '@/src/constants';
import { fireblocksClient } from '@/src/helpers';
import { ExecuteXrplTransactionRequest } from '@/src/models';
import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { ErrorResponse, RipplePathFindRequest, RipplePathFindResponse } from 'xrpl';
import { useXRPLContext } from '../../../contexts';
import { isXRPLErrorCode, isXRPLErrorResponse, KnownXrplErrorCodes } from './xrpl.helpers';
import { XrplQueryKeys } from './xrpl.types';

export const useExecuteXrplTransaction = () => {
  return useMutation({
    mutationFn: (data: ExecuteXrplTransactionRequest) =>
      fireblocksClient.post(API_ENDPOINTS.XRPL, data).then((res) => res.data),
    mutationKey: [XrplQueryKeys.ExecuteXrplTransaction],
  });
};

/**
 * The ripple_path_find method is a simplified version of the path_find method that provides a single response with a payment path you can use right away.
 * It is available in both the WebSocket and JSON-RPC APIs. However, the results tend to become outdated as time passes.
 * Instead of making multiple calls to stay updated, you should instead use the path_find method to subscribe to continued updates where possible.
 *
 * Although the rippled server tries to find the cheapest path or combination of paths for making a payment, it is not guaranteed that the paths returned by this method are, in fact, the best paths.
 *
 * @param params - The parameters for the ripple_path_find method.
 * @returns The result of the ripple_path_find method.
 */
export const useRipplePathFind = (
  options?: UseMutationOptions<
    RipplePathFindResponse['result'], // TData
    ErrorResponse, // TError
    RipplePathFindRequest // TVariables
  >
) => {
  const { client } = useXRPLContext();

  return useMutation({
    mutationKey: [XrplQueryKeys.RipplePathFind],
    mutationFn: async (params: RipplePathFindRequest) => {
      return await client
        .request({ ...params, command: XrplQueryKeys.RipplePathFind })
        .then((res) => res.result)
        .catch((err) => {
          if (isXRPLErrorResponse(err)) {
            if (isXRPLErrorCode(err.data, KnownXrplErrorCodes.InvalidParams)) {
              return {} as RipplePathFindResponse['result'];
            }
          }
          throw err.data;
        });
    },
    ...options,
  });
};
